import SuccessGreenAnimationJson from 'assets/ver2/lotties/success-green-animation.json';
import ButtonCommon from 'components/common/ButtonCommon';
import SizeBox from 'components/common/SizeBox';
import {
  DESKTOP_MIN_WIDTH,
  MOBILE_MIN_WIDTH,
  TABLET_MAX_WIDTH,
} from 'constants';
import {
  PII_PROFILE_PERSONAL_DETAILS_FISTNAME_NAME,
  PII_PROFILE_PERSONAL_DETAILS_LASTNAME_NAME,
} from 'features/pii/const';
import usePIIStore from 'features/pii/hooks/usePIIStore';
import { useDetectDevice } from 'hooks/useDetectDevice';
import useLang from 'hooks/useLang';
import usePortalModal from 'hooks/usePortalModal';
import Lottie from 'lottie-react';
import Modal from 'react-bootstrap/Modal';
import styled from 'styled-components';
import {
  displayFlexColCenterCenterMixin,
  displayFlexRowCenterCenterMixin,
  displayGridCenterCenterMixin,
  fontSize24Mixin,
  paragraph500Mixin,
  scrollbarThumbMixin,
  textBreakLineWordMixin,
} from 'styles';

const PROGRESS_COUNTDOWN = 2;

const FormPIIVerificationSuccessfulPopup = ({
  isOpen,
  isBackdropOverlap,
  zIndexBackdropOverlap,
  zIndexModal = 0,
}) => {
  const { t } = useLang();
  const { isDesktop } = useDetectDevice();
  const { closePortalModalName } = usePortalModal();

  const { personalDetails, resetSignupPIIInformation } = usePIIStore();

  const getUserFullName = () => {
    return `${personalDetails[PII_PROFILE_PERSONAL_DETAILS_FISTNAME_NAME]} ${personalDetails[PII_PROFILE_PERSONAL_DETAILS_LASTNAME_NAME]}`;
  };

  const handleOnClickDoneButton = () => {
    console.log(
      `⭐ FormPIIVerificationSuccessfulPopup 👉 handleOnClickDoneButton ↩`
    );
    resetSignupPIIInformation();
    closePortalModalName();
  };

  return (
    <>
      <style>
        {zIndexModal &&
          `
          .form-pii-profile-successful-modal {
            z-index: ${zIndexModal || '1101'};
          }
        `}
        {isBackdropOverlap
          ? `
          .form-pii-profile-successful-modal-backdrop {
            z-index: ${zIndexBackdropOverlap || '1100'};
            background: #212121;
            opacity: 0.7!important;
            display: initial!important;
          }
        `
          : `
        .form-pii-profile-successful-modal-backdrop {
          background: #212121;
          opacity: 0.7!important;
          display: initial!important;
        }
      `}
      </style>
      <ModalStyled
        show={isOpen}
        onHide={() => {}}
        aria-labelledby="contained-modal-title-vcenter"
        className="form-pii-profile-successful-modal"
        backdropClassName="form-pii-profile-successful-modal-backdrop"
        centered
      >
        <ModalBody>
          <FormPIIProfileVerificationSuccessfulStyled>
            <BodyContent>
              <FormImage>
                <Lottie
                  animationData={SuccessGreenAnimationJson}
                  loop={false}
                  style={{
                    width: '300px',
                    height: '300px',
                  }}
                />
              </FormImage>
              <FormTitle>
                {t('login_text_greeting_1')}
                <br />
                {getUserFullName()}
              </FormTitle>
              <SizeBox height={10} />
              <FormDesc
                dangerouslySetInnerHTML={{
                  __html: t('form_pii_profile_verification_successful_desc'),
                }}
              />
              <SizeBox height={isDesktop ? 20 : 30} />
              <ButtonCommon
                value={t('button_done')}
                color="var(--c-primary)"
                background="var(--bg-primary)"
                isFill={true}
                styles={{
                  margin: '0px',
                  height: '50px',
                  width: isDesktop ? '100%' : '50%',
                }}
                onClick={handleOnClickDoneButton}
                isUseKeyDown
              />
            </BodyContent>
          </FormPIIProfileVerificationSuccessfulStyled>
        </ModalBody>
      </ModalStyled>
    </>
  );
};

const ModalStyled = styled(Modal)`
  padding-left: 0px !important;
  padding-right: 0px !important;

  @media screen and (min-width: ${MOBILE_MIN_WIDTH}px) and (max-width: ${TABLET_MAX_WIDTH -
    1}px) {
    & .modal-dialog {
      position: absolute;
      margin: 0;
      top: 0px;
      height: 100dvh;
      width: 100%;
      max-width: 100%;
      align-items: end;
      transition: ease-out 0.3s !important;
      transform: none;

      & .modal-content {
        padding: 0px 16px;
        box-shadow: none;
        background: var(--ds-c-white);
        width: 100%;
        min-height: calc(100dvh - 70px);
        max-height: calc(100dvh - 70px);
        margin-inline: auto;
        border: none;
        border-radius: 0;
      }
    }
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    & .modal-dialog {
      transform: none;
      & .modal-content {
        padding: 50px 40px 40px 40px;
        box-shadow: var(--ds-bs-4);
        background: var(--ds-c-white);
        width: 400px;
        height: fit-content;
        margin-inline: auto;
        border: none;
        border-radius: 20px;
      }
    }
  }
`;

const ModalBody = styled(Modal.Body)`
  padding: 0;
  text-align: left;
  margin-bottom: 0px;
  overflow-x: auto;
  margin-inline: -16px;
  padding-inline: 16px;
  ${scrollbarThumbMixin};

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    overflow-x: unset;
  }
`;

const FormPIIProfileVerificationSuccessfulStyled = styled.div`
  ${displayFlexColCenterCenterMixin};
  width: 100%;
  height: calc(100dvh - 70px);

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    height: 100%;
  }
`;

const BodyContent = styled.div`
  ${displayGridCenterCenterMixin};
`;
const SpinnerProgressStyled = styled.div`
  width: 30px;
  aspect-ratio: 1;
  border: 2.5px solid #1aa75830;
  border-radius: 50%;
  position: relative;
  transform: rotate(45deg);
  margin-inline: auto;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    width: 60px;
  }

  &::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 50%;
    border: 2.5px solid #1aa758;
    animation: l18 ${PROGRESS_COUNTDOWN}s linear;
  }
  @keyframes l18 {
    0% {
      clip-path: polygon(50% 50%, 0 0, 0 0, 0 0, 0 0, 0 0);
    }
    25% {
      clip-path: polygon(50% 50%, 0 0, 100% 0, 100% 0, 100% 0, 100% 0);
    }
    50% {
      clip-path: polygon(50% 50%, 0 0, 100% 0, 100% 100%, 100% 100%, 100% 100%);
    }
    75% {
      clip-path: polygon(50% 50%, 0 0, 100% 0, 100% 100%, 0 100%, 0 100%);
    }
    100% {
      clip-path: polygon(50% 50%, 0 0, 100% 0, 100% 100%, 0 100%, 0 0);
    }
  }
`;
const FormTitle = styled.h1`
  ${paragraph500Mixin};
  ${fontSize24Mixin};
  font-family: var(--ff-quaternary);
  text-align: center;
`;
const FormDesc = styled.p`
  ${paragraph500Mixin};
  text-align: center;
  ${textBreakLineWordMixin};
`;

const FormImage = styled.div`
  ${displayFlexRowCenterCenterMixin};
  width: 100%;
  margin-bottom: -60px;
`;

export default FormPIIVerificationSuccessfulPopup;
